import React, { useEffect, useState } from 'react';
import TradingViewChartIcon from './TradingViewChartIcon';
import './TopVolumeView.css';
import { ensureArray } from './config';

const TopVolumeView = ({ data }) => {
  const safeData = ensureArray(data);
  const [excludeMajors, setExcludeMajors] = useState(false);
  const [rankSymbol, setRankSymbol] = useState('');

  const timeFrames = [...new Set(safeData.map(d => d.timeFrame))].sort((a, b) => a - b);
  const MAJOR_COINS = new Set(['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT','ADAUSDT','DOGEUSDT','LINKUSDT','PEPEUSDT']);

  // All symbols for search box (respect excludeMajors toggle)
  const allSymbols = [...new Set(safeData.map(d => d.symbol))]
    .filter(sym => !(excludeMajors && MAJOR_COINS.has(sym)))
    .sort();

  // ---- helpers for rank calc (same numeric logic) ----
  function toNumber(v) {
    if (v === null || v === undefined) return 0;
    const cleaned = String(v).replace(/,/g, '').replace('%', '').trim();
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }

  function getRealVolumeUSDT(item) {
    return toNumber(item.volume) * toNumber(item.marketPrice);
  }

  // Rank summary per timeframe for selected symbol
  const rankSummary = (() => {
    const sym = (rankSymbol || '').trim().toUpperCase();
    if (!sym) return [];

    return timeFrames.map(tf => {
      const rows = safeData
        .filter(d => d.timeFrame === tf)
        .filter(d => !(excludeMajors && MAJOR_COINS.has(d.symbol)));

      // sort by real volume desc (your default ranking)
      const sorted = [...rows].sort((a, b) => getRealVolumeUSDT(b) - getRealVolumeUSDT(a));

      const idx = sorted.findIndex(x => String(x.symbol).toUpperCase() === sym);
      const found = idx >= 0 ? sorted[idx] : null;

      return {
        timeFrame: tf,
        rank: idx >= 0 ? idx + 1 : '-',
        realVolume: found ? getRealVolumeUSDT(found) : null,
        change: found ? found.change : null
      };
    });
  })();

  // if majors excluded, and user typed a major symbol, keep it but it'll show "-" ranks (fine)
  // if you prefer auto-clear, tell me.

  return (
    <div className="top-volume-root">
      {/* ✅ Global Bar */}
      <div className="top-volume-bar">
        <label className="bar-item">
          <input
            type="checkbox"
            checked={excludeMajors}
            onChange={(e) => setExcludeMajors(e.target.checked)}
          />
          <span>Exclude Majors (BTC, ETH, SOL, XRP, ADA, DOGE, LINK, PEPE)</span>
        </label>

        {/* ✅ Rank Symbol Search */}
        <div className="bar-item">
          <span className="bar-label">Rank check:</span>
          <input
            className="rank-input"
            list="allSymbols"
            placeholder="e.g. AEROUSDT"
            value={rankSymbol}
            onChange={(e) => setRankSymbol(e.target.value)}
          />
          <datalist id="allSymbols">
            {allSymbols.map(s => <option key={s} value={s} />)}
          </datalist>
        </div>

        {/* ✅ Rank Summary Box */}
        {rankSymbol.trim() && (
          <div className="rank-summary">
            <div className="rank-title">
              {rankSymbol.toUpperCase()} ranks
            </div>

            <div className="rank-grid">
              {rankSummary.map(r => (
                <div key={r.timeFrame} className="rank-pill">
                  <div className="rank-tf">{r.timeFrame}m</div>
                  <div className="rank-val">#{r.rank}</div>
                  {r.realVolume !== null && (
                    <div className="rank-sub">
                      {r.realVolume.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      {r.change ? ` | ${r.change}` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ✅ Tables Grid Wrapper (keeps your 2-col layout here only) */}
      <div className="volume-view-container">
        {timeFrames.map((tf) => (
          <TimeFrameSection
            key={tf}
            timeFrame={tf}
            data={safeData.filter(d => d.timeFrame === tf)}
            excludeMajors={excludeMajors}
          />
        ))}
      </div>
    </div>
  );
};



const TimeFrameSection = ({ timeFrame, data, excludeMajors }) => {
  const safeData = ensureArray(data);

  const [filters, setFilters] = useState({ symbol: '' });
  const MAJOR_COINS = new Set(['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'XRPUSDT','ADAUSDT','DOGEUSDT','LINKUSDT','PEPEUSDT']);

  const [expanded, setExpanded] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'realVolumeUSDT', direction: 'desc' });

  const symbols = [...new Set(
    safeData
      .map(d => d.symbol)
      .filter(sym => !(excludeMajors && MAJOR_COINS.has(sym)))
  )];


const filtered = safeData.filter(item => {
  if (excludeMajors && MAJOR_COINS.has(item.symbol)) return false;
  if (filters.symbol && item.symbol !== filters.symbol) return false;
  return true;
});

useEffect(() => {
  if (excludeMajors && filters.symbol && MAJOR_COINS.has(filters.symbol)) {
    setFilters({ symbol: '' });
  }
}, [excludeMajors, filters.symbol]);


// ✅ HOISTED (no TDZ issue)
function getRealVolumeUSDT(item) {
  const vol = toNumber(item.volume);
  const price = toNumber(item.marketPrice);
  return vol * price;
}

// ✅ handles "123", "123.45", "", null, "1,234,567"
function toNumber(v) {
  if (v === null || v === undefined) return 0;

  const cleaned = String(v)
    .replace(/,/g, '')
    .replace('%', '')
    .trim();

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}


function getSortValue(item, key) {
  if (key === 'realVolumeUSDT') return getRealVolumeUSDT(item);
  if (key === 'symbol') return String(item.symbol || '');
  return toNumber(item[key]);
}

const sorted = [...filtered].sort((a, b) => {
  const aVal = getSortValue(a, sortConfig.key);
  const bVal = getSortValue(b, sortConfig.key);

  // ✅ string sort for symbol
  if (sortConfig.key === 'symbol') {
    return sortConfig.direction === 'asc'
      ? aVal.localeCompare(bVal)
      : bVal.localeCompare(aVal);
  }

  // ✅ numeric sort
  return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
});

// ✅ make numeric columns default to DESC on first click
const handleSort = (key) => {
  setSortConfig(prev => {
    if (prev.key === key) {
      return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    }

    const numericKeys = new Set(['marketPrice', 'volume', 'change', 'adx', 'realVolumeUSDT']);
    return { key, direction: numericKeys.has(key) ? 'desc' : 'asc' };
  });
};

  const displayData = expanded ? sorted : sorted.slice(0, 5);


  return (
    <div className="tf-section">
      <h3>Top Volume – {timeFrame} min</h3>
      <div className="filters">
        <select value={filters.symbol} onChange={e => setFilters({ ...filters, symbol: e.target.value })}>
          <option value="">Symbol</option>
          {symbols.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {filtered.length > 5 && (
          <button className="expand-button" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        )}
        <button onClick={() => setFilters({ symbol: '' })}>Clear Filters</button>
      </div>

      <div className="volume-table-wrapper">
        <table className="volume-table">
          <thead>
            <tr>
              <th>#</th>
              <th onClick={() => handleSort('symbol')}>Symbol</th>
              <th>Chart</th>
              <th onClick={() => handleSort('marketPrice')}>Market Price</th>
              <th onClick={() => handleSort('volume')}>Volume</th>
              <th onClick={() => handleSort('realVolumeUSDT')}>Volume[USDT]</th> 
              <th onClick={() => handleSort('change')}>Change %</th>
              <th onClick={() => handleSort('changePctRank')}>Change % Rank</th>
              <th onClick={() => handleSort('adx')}>ADX</th>
            </tr>
          </thead>
          <tbody>
            {displayData.map((item, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>{item.symbol}</td>
                <td>
                  <TradingViewChartIcon
                    symbol={item.symbol}
                    timeFrame={item.timeFrame}
                    exchange={item.exchange}
                  />
                </td>                
                <td>{item.marketPrice}</td>
                <td>{item.volume}</td>
                <td>{getRealVolumeUSDT(item).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                <td>{item.change}</td>
                <td>{item.changePctRank}</td>
                <td>{item.adx}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TopVolumeView;
